/**
 * Formatage d'un nombre d'octets pour l'affichage (#180).
 *
 * Base 1024, comme le calcul du plafond côté API (`FREE_TIER_STORAGE_BYTES =
 * 10 * 1024 ** 3`). Il n'y a qu'une définition du gigaoctet dans le produit, et
 * c'est celle qui décide du refus : formater en base 1000 ferait afficher
 * « 10,7 Go » à côté d'un plafond annoncé à 10 Go.
 */
const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const

/** Libellés d'unité par langue : « o » en français, « B » en anglais. */
const UNIT_LABELS: Record<string, readonly string[]> = {
  fr: ['o', 'Ko', 'Mo', 'Go', 'To'],
  en: UNITS,
}

/**
 * Rend par exemple `3,2 Go` (fr) ou `3.2 GB` (en). Une décimale sous 100,
 * aucune au-delà : « 512 Mo » se lit mieux que « 512,0 Mo », et la précision
 * n'apporte rien à cette échelle.
 */
export function formatBytes(bytes: number, locale: string): string {
  const labels = UNIT_LABELS[locale === 'en' ? 'en' : 'fr']
  let value = Math.max(0, bytes)
  let unit = 0
  while (value >= 1024 && unit < labels.length - 1) {
    value /= 1024
    unit += 1
  }

  const fractionDigits = unit === 0 || value >= 100 ? 0 : 1
  const formatted = new Intl.NumberFormat(locale === 'en' ? 'en-GB' : 'fr-FR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)

  return `${formatted} ${labels[unit]}`
}

/**
 * Part de quota consommée, bornée à [0, 100] pour l'affichage d'une jauge.
 *
 * Bornée à 100 volontairement : une organisation peut dépasser son plafond
 * (plafond abaissé après coup), et une barre de progression à 130 % déborderait
 * de son conteneur. Le dépassement se dit avec un libellé, pas avec la barre.
 */
export function usagePercent(usedBytes: number, quotaBytes: number): number {
  if (quotaBytes <= 0) return 0
  return Math.min(100, Math.max(0, (usedBytes / quotaBytes) * 100))
}
